import type {
  IMapLayoutCatalog,
  IMapLayoutElement,
  IMapLayoutElementGeometry,
} from '@interfaces/map';

// UE owns the catalog of pre-built room elements and the arrangement — it auto-chains
// rooms by their exits. The web is only a picker: it chooses and orders element ids and
// never authors geometry, so every geometry field below is carried for display/debug
// only and is deliberately not sent back in SPAWN_LAYOUT.
//
// At runtime the catalog comes from the build: the MytaMapEditor GameCast metadata
// provider emits it under plugins.mapEditor, spatial-manager projects it onto the map
// record, and the hub reads map.layoutCatalog. A map whose build shipped none has no map
// editor at all — see resolveCatalogElements and useCanUseMapEditor.
export interface MapEditorCatalogElement {
  id: string;
  displayName: string;
  category: string;
  thumbnailUrl: string;
  type: string;
  size: string;
  // Empty means the element is catalogued but has no room level built yet — UE spawns
  // nothing for it. Surfaced in the tree so an author is not left wondering why a room
  // they picked never appeared.
  geometryLevel: string;
  geometryClass: string;
  overrideExit: boolean;
  exitOffset: number[];
  exitYawDeg: number;
  // The build's own verdict on this element's geometry. Optional because the field post-dates the
  // rest of the catalog, so every reader below tolerates it missing rather than treating absence
  // as a negative answer.
  geometry?: IMapLayoutElementGeometry;
}

// 'fallback' means the map's build shipped no catalog. It carries no elements: useCanUseMapEditor
// gates every map-editor surface on 'build', so on a map without a kit there is no picker to fill,
// no inspector to look up ids in, and nothing to publish. A stand-in list existed here once and
// could not be reached from anywhere — worse, its ids were the test map's, so anything that did
// reach it would have offered rooms the map cannot build.
export type CatalogSource = 'build' | 'fallback';

const NO_CATALOG = {
  elements: [] as MapEditorCatalogElement[],
  source: 'fallback' as const,
};

const normalizeElement = (
  element: IMapLayoutElement,
): MapEditorCatalogElement => ({
  id: element.id,
  displayName: element.displayName || element.id,
  // Ungrouped elements would otherwise vanish from a category-keyed tree.
  category: element.category || 'Other',
  thumbnailUrl: element.thumbnailUrl ?? '',
  type: element.type ?? '',
  size: element.size ?? '',
  geometryLevel: element.geometryLevel ?? '',
  geometryClass: element.geometryClass ?? '',
  overrideExit: element.overrideExit ?? false,
  exitOffset: element.exitOffset ?? [0, 0, 0],
  exitYawDeg: element.exitYawDeg ?? 0,
  // Passed through rather than flattened into booleans here: the shape is the build's, new
  // fields appear in it without a UE code change, and normalising early would drop them.
  geometry: element.geometry,
});

// Defensive on purpose: this payload crosses UE → metadata.json → spatial-manager →
// DB → API before arriving, so anything malformed falls back rather than throwing
// inside a render. An entry without an id is unusable — it is what SPAWN_LAYOUT sends.
export const resolveCatalogElements = (
  catalog: IMapLayoutCatalog | undefined | null,
): { elements: MapEditorCatalogElement[]; source: CatalogSource } => {
  const rawElements = catalog?.elements;
  if (!Array.isArray(rawElements)) {
    return NO_CATALOG;
  }

  const elements = rawElements
    .filter(
      (element): element is IMapLayoutElement =>
        !!element && typeof element.id === 'string' && element.id.length > 0,
    )
    .map(normalizeElement);

  // An empty array from the build is treated as "no catalog shipped" rather than
  // "a catalog of nothing" — the latter would leave the author with a dead panel
  // and no signal about why.
  if (!elements.length) {
    return NO_CATALOG;
  }

  return { elements, source: 'build' };
};

export const findCatalogElement = (
  elements: MapEditorCatalogElement[],
  id: string,
): MapEditorCatalogElement | undefined =>
  elements.find((element) => element.id === id);

// Whether this build can actually put art on the ground for this element.
//
// Prefers the build's own verdict over the declared path, because the two disagree in the case
// that matters: an element can name a room level the build never cooked, and the declaration
// looks fine while the spawn comes out a placeholder box.
export const hasBuiltGeometry = (element: MapEditorCatalogElement): boolean => {
  const { geometry } = element;
  if (!geometry) {
    // A build that predates the verdict field. The declared level is the only evidence there is —
    // which is what this check has always used.
    return (element.geometryLevel ?? '').trim().length > 0;
  }

  // `cooked` is conditionally absent by design (class geometry, or an unreadable cook list) and
  // absent means unknown, so only an explicit false counts against it.
  return (
    geometry.kind !== 'greybox' &&
    geometry.available &&
    geometry.cooked !== false
  );
};

// The element declares art this build cannot supply — a level that does not resolve, or one left
// out of the cook. UE greyboxes it, so the author gets a placeholder box where they expect a
// room, and nothing else in the UI would say why.
//
// Deliberately distinct from "no art yet" (`kind: 'greybox'`), which is an intentional state and
// not a problem to report. Only a build can tell these apart, so this returns false whenever the
// verdict is missing rather than guessing from the declared path.
export const isGeometryMisconfigured = (
  element: MapEditorCatalogElement,
): boolean => {
  const { geometry } = element;
  if (!geometry || geometry.kind === 'greybox') {
    return false;
  }

  return !geometry.available || geometry.cooked === false;
};

export interface MapEditorCatalogGroup {
  category: string;
  elements: MapEditorCatalogElement[];
}

// Grouped for the tree. Categories are sorted, but elements keep their catalog order
// so authors see S/M/L in the sequence UE lists them rather than alphabetically.
export const getCatalogGroups = (
  elements: MapEditorCatalogElement[],
): MapEditorCatalogGroup[] => {
  const byCategory = new Map<string, MapEditorCatalogElement[]>();
  elements.forEach((element) => {
    const bucket = byCategory.get(element.category);
    if (bucket) {
      bucket.push(element);
      return;
    }
    byCategory.set(element.category, [element]);
  });

  return Array.from(byCategory.keys())
    .sort((a, b) => a.localeCompare(b))
    .map((category) => ({
      category,
      elements: byCategory.get(category) ?? [],
    }));
};
